import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "./Reveal";
import { SERVICES } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "bg-[#F2EDE1]/5 border-[#F2EDE1]/15 text-[#F2EDE1] placeholder:text-[#F2EDE1]/35 rounded-none px-5 py-4 h-auto focus-visible:ring-[#A6874E] focus-visible:border-[#A6874E] transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received — we'll get back to you within one business day.");
      setForm({ name: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0B3D2E] py-24 md:py-36" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-24">
        <div>
          <Reveal>
            <span className="eyebrow text-[#A6874E]">Start a Conversation</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              className="mt-8 font-grotesk font-bold uppercase text-[#F2EDE1] leading-[0.98] tracking-tight text-4xl sm:text-5xl md:text-6xl"
              data-testid="contact-heading"
            >
              Let's talk about <em className="font-display italic font-medium normal-case text-[#A6874E]">growth.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-xs md:text-sm leading-relaxed text-[#F2EDE1]/60 max-w-md">
              Tell us where your business is and where it needs to be. We'll reply with a
              strategy-first view of your next move — no obligation, no jargon.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 space-y-4">
              <div className="flex items-center gap-4">
                <span className="w-1.5 h-1.5 bg-[#A6874E]" />
                <span className="eyebrow text-[#F2EDE1]/70">Replies within one business day</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-1.5 h-1.5 bg-[#A6874E]" />
                <span className="eyebrow text-[#F2EDE1]/70">Working worldwide, fully remote</span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Input
                placeholder="Your name"
                value={form.name}
                onChange={set("name")}
                className={inputCls}
                data-testid="enquiry-name-input"
                required
              />
              <Input
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={set("email")}
                className={inputCls}
                data-testid="enquiry-email-input"
                required
              />
            </div>
            <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
              <SelectTrigger className={`${inputCls} w-full`} data-testid="enquiry-service-select">
                <SelectValue placeholder="Service you need" />
              </SelectTrigger>
              <SelectContent className="bg-[#093125] border-[#F2EDE1]/15 text-[#F2EDE1] rounded-none">
                {SERVICES.map((s) => (
                  <SelectItem
                    key={s.id}
                    value={s.title}
                    className="focus:bg-[#A6874E] focus:text-[#0B3D2E]"
                    data-testid={`enquiry-service-option-${s.id}`}
                  >
                    {s.title}
                  </SelectItem>
                ))}
                <SelectItem value="Something else" className="focus:bg-[#A6874E] focus:text-[#0B3D2E]" data-testid="enquiry-service-option-other">
                  Something else
                </SelectItem>
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Tell us about your next move — goals, timeline, anything that matters."
              value={form.message}
              onChange={set("message")}
              rows={5}
              className={`${inputCls} resize-none`}
              data-testid="enquiry-message-input"
              required
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="group w-full flex items-center justify-center gap-3 bg-[#A6874E] text-[#0B3D2E] px-8 py-5 eyebrow hover:bg-[#F2EDE1] disabled:opacity-60 transition-colors duration-300"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending
                </>
              ) : (
                <>
                  Send Enquiry
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
